import { FC, useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import './FormAppHome.scss';
import { BvIcon } from 'bevi-icon';
import { schema } from './Validate';
import ErrorMessage from '../../ErrorMessage/ErrorMessage';
import { useAuth } from '../../../providers/AuthProvider';
import { useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import { ArticlesTypeaheadService } from '../../../services/Articles/ArticlesTypeahead/ArticlesTypeaheadService';
import { ArticleTypeaheadModel } from '../../../models/Articles/ArticleTypeahead/ArticleTypeaheadModel';
import highlightMatches from '../../../utils/HighlightMatches';

interface FormAppHomeProps {
  search?: string;
  disable?: boolean;
}

const TYPEAHEAD_MIN_CHARS = 3;
const TYPEAHEAD_DEBOUNCE_MS = 300;
const TYPEAHEAD_LIMIT = 8;

const typeaheadService = new ArticlesTypeaheadService();

const FormAppHome: FC<FormAppHomeProps> = ({ search, disable = false }) => {
  const navigate = useNavigate();

  const { onInvalid } = useAuth();

  const { handleSubmit, register, reset, formState: { errors } } = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: yupResolver(schema),
  });

  const [query, setQuery] = useState<string>(search ?? '');
  const [suggestions, setSuggestions] = useState<ArticleTypeaheadModel[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const requestSeqRef = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const onSubmit = async (data: any) => {
    reset();
    setQuery('');
    setIsDropdownOpen(false);
    const encodedSearch = encodeURIComponent(data.search);
    navigate(`/articles/search/${encodedSearch}`);
  }

  const fetchSuggestions = async (value: string) => {
    const seq = ++requestSeqRef.current;
    setIsLoadingSuggestions(true);

    const [response, error] = await typeaheadService.suggest(value, TYPEAHEAD_LIMIT);
    if (seq !== requestSeqRef.current) return;

    if (error) {
      setSuggestions([]);
      setIsDropdownOpen(false);
    } else {
      setSuggestions(response?.data || []);
      setIsDropdownOpen(true);
    }

    setIsLoadingSuggestions(false);
  }

  const onChangeSearch = (value: string) => {
    setQuery(value);
    setActiveIndex(-1);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = value.trim();
    if (trimmed.length < TYPEAHEAD_MIN_CHARS) {
      requestSeqRef.current += 1;
      setIsLoadingSuggestions(false);
      setSuggestions([]);
      setIsDropdownOpen(false);
      return;
    }

    debounceRef.current = setTimeout(() => fetchSuggestions(trimmed), TYPEAHEAD_DEBOUNCE_MS);
  }

  const goToArticle = (articleId: number) => {
    setIsDropdownOpen(false);
    setQuery('');
    reset();
    navigate(`/articles/view/${articleId}`);
  }

  const onKeyDownSearch = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isDropdownOpen || !suggestions.length) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => (prev + 1) % suggestions.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
      return;
    }

    if (event.key === 'Enter' && activeIndex >= 0) {
      event.preventDefault();
      goToArticle(suggestions[activeIndex].id);
      return;
    }

    if (event.key === 'Escape') {
      setIsDropdownOpen(false);
    }
  }

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const { onChange: rhfOnChange, ...registerRest } = register('search');

  const renderDropdown = () => {
    if (!isDropdownOpen) return null;

    return (
      <div className="typeahead-dropdown">
        {isLoadingSuggestions ? (
          <div className="typeahead-loading">
            <Skeleton height={20} className="mb-2" />
            <Skeleton height={20} className="mb-2" />
            <Skeleton height={20} />
          </div>
        ) : suggestions.length > 0 ? (
          <ul className="typeahead-list">
            {suggestions.map((item, index) => (
              <li
                key={item.id}
                className={`typeahead-item ${index === activeIndex ? 'active' : ''}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  goToArticle(item.id);
                }}
                onMouseEnter={() => setActiveIndex(index)}
              >
                <div className="typeahead-item-title">
                  {highlightMatches(item.title, query)}
                </div>
                {(item.category || item.section) && (
                  <div className="typeahead-item-breadcrumb">
                    {[item.category, item.section].filter(Boolean).join(' > ')}
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div className="typeahead-empty">Nenhuma sugestão encontrada</div>
        )}
      </div>
    );
  }

  return (
    <div className="FormAppHome" data-testid="FormAppHome" ref={containerRef}>
      <form className="w-100" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <div className={`input-group mt-0 ${onInvalid(errors?.search)}`}>
          <input
            type="text"
            className="form-control"
            placeholder="Busque aqui..."
            defaultValue={search ?? ''}
            {...registerRest}
            onChange={(event) => {
              rhfOnChange(event);
              onChangeSearch(event.target.value);
            }}
            onKeyDown={onKeyDownSearch}
            onFocus={() => {
              if (suggestions.length || isLoadingSuggestions) setIsDropdownOpen(true);
            }}
            disabled={disable}
          />
          <button
            type="submit"
            className="input-group-text"
            disabled={disable}
          >
            <BvIcon
              name="search"
              variant="solid"
              weight={600}
              width={16}
              height={16}
            />
          </button>
        </div>
        {renderDropdown()}
        <div className="w-100">
          <ErrorMessage name={'Texto'} type={errors?.search?.type?.toString()} min={3} max={255} />
        </div>
      </form>
    </div>
  );
}

export default FormAppHome;
